import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { X, Check, Crown, Sparkles, BookOpen, Brain, TrendingUp, MessageCircle } from 'lucide-react'

interface SubscribeModalProps {
  isOpen: boolean
  onClose: () => void
  onSubscribe: () => void
}

const BENEFITS = [
  { icon: BookOpen, text: 'All 6 units in every chapter' },
  { icon: Brain, text: 'Personalized AI feedback' },
  { icon: MessageCircle, text: 'Vocabulary & grammar lessons' },
  { icon: TrendingUp, text: 'Progress tracking & streaks' },
]

type PlanOption = 'monthly' | 'yearly'

export function SubscribeModal({ isOpen, onClose, onSubscribe }: SubscribeModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<PlanOption>('yearly')

  if (!isOpen) return null

  const handleSubscribe = () => {
    // Placeholder for RevenueCat integration
    console.log(`Subscribing to ${selectedPlan} plan`)
    onSubscribe()
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-bounce-in">
        {/* Top gradient bar */}
        <div className="h-2 bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500" />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>

        <div className="p-6 pt-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 text-white mb-4 shadow-lg shadow-purple-500/30">
              <Crown className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Unlock Your Full Journey
            </h2>
            <p className="text-gray-500">
              Continue learning with unlimited access
            </p>
          </div>

          {/* Benefits list */}
          <div className="space-y-3 mb-6">
            {BENEFITS.map((benefit, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-3 rounded-xl bg-violet-50/50"
              >
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-4 h-4 text-violet-600" />
                </div>
                <span className="text-gray-700">{benefit.text}</span>
                <Check className="w-4 h-4 text-emerald-500 ml-auto" />
              </div>
            ))}
          </div>

          {/* Plan options */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {/* Monthly */}
            <button
              onClick={() => setSelectedPlan('monthly')}
              className={`
                relative p-4 rounded-2xl border-2 transition-all duration-200
                ${selectedPlan === 'monthly'
                  ? 'border-violet-500 bg-violet-50'
                  : 'border-gray-200 hover:border-violet-200'
                }
              `}
            >
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Monthly</p>
                <p className="text-xl font-bold text-gray-800">$9.99</p>
                <p className="text-xs text-gray-400">/month</p>
              </div>
            </button>

            {/* Yearly */}
            <button
              onClick={() => setSelectedPlan('yearly')}
              className={`
                relative p-4 rounded-2xl border-2 transition-all duration-200
                ${selectedPlan === 'yearly'
                  ? 'border-violet-500 bg-violet-50'
                  : 'border-gray-200 hover:border-violet-200'
                }
              `}
            >
              {/* Save badge */}
              <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-bold rounded-full shadow-md">
                SAVE 50%
              </div>

              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Yearly</p>
                <p className="text-xl font-bold text-gray-800">$59.99</p>
                <p className="text-xs text-gray-400">/year</p>
              </div>
            </button>
          </div>

          {/* Subscribe button */}
          <Button
            onClick={handleSubscribe}
            className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 transition-all duration-300 rounded-xl"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            Subscribe Now
          </Button>

          {/* Footer note */}
          <p className="text-center text-xs text-gray-400 mt-4">
            Payment handled securely by RevenueCat
          </p>
        </div>
      </div>
    </div>
  )
}
